/**
 * Copilot conversation state, driven by the same event protocol as
 * botcircuits-agent-builder-copilot (see its README "API" section); on the runtime those
 * events are produced by model/runtime-events.js. Pure functions only, so the UI logic is
 * unit-tested without sockets.
 *
 * Event: { type, runId, seq, ts, data }
 *   run.started · message.delta{text} · tool.started{id,name,agent} · tool.finished{id,ok,error}
 *   subagent.started{id,subagent,task} · subagent.finished{id,ok,summary} · todos.updated{todos}
 *   resource.changed{kind,action,id,name} · approval.required{requests}
 *   upload.requested{dataSourceId,description,ocr,accept[]}  (an upload card; see components/upload-card)
 *   action{type: "ROUTE", route, path, params, label, handoffMessage?}
 *   onboarding.card{kind, text, ...}   (an onboarding question or the use cases; see model/onboarding)
 *   onboarding.setup{message, websites, documents}   (the agent is being set up in the background)
 *   publish.card{status, text, versionName, snippet, ...}   (the outcome of publishing; see model/publishing)
 *   suggestions{items: [{label, message}]}   (after run.completed; quick-reply buttons)
 *   run.completed{status,text} · run.failed{error}
 */

import { sanitizeTesting } from './testing';
import { sanitizeClarification } from './clarification';
import { sanitizeOnboardingCard, sanitizeSetup } from './onboarding';
import { sanitizePublishCard } from './publishing';

export const initialState = { messages: [], running: false, awaitingApproval: false, error: null };

const TOOL_LABELS = {
    request_clarification: 'Preparing a question',
    generate_test_plan: 'Preparing test scenarios',
    get_test_plan: 'Loading test scenarios',
    save_test_plan: 'Saving test scenarios',
    start_test_run: 'Starting agent tests',
    get_test_run: 'Checking test results',
    fetch_website: 'Learning about your business',
    request_onboarding_websites: 'Preparing a question',
    request_onboarding_documents: 'Preparing a question',
    start_onboarding_setup: 'Starting your agent setup',
    gather_onboarding_material: 'Reading your website and documents',
    suggest_onboarding_use_cases: 'Preparing use cases for your business',
    get_publish_status: 'Checking published versions',
    publish_agent: 'Publishing your agent',
    get_app_overview: 'Reviewing your agent',
    get_agent_instructions: 'Reading instructions',
    update_agent_instructions: 'Updating instructions',
    update_core_settings: 'Updating settings',
    recommend_implementation: 'Choosing the right approach',
    list_agent_tools: 'Checking tools',
    get_agent_tool: 'Reading a tool',
    create_agent_tool: 'Creating a tool',
    update_agent_tool: 'Updating a tool',
    delete_agent_tool: 'Deleting a tool',
    list_skills: 'Checking skills',
    get_skill: 'Reading a skill',
    create_skill: 'Creating a skill',
    update_skill: 'Updating a skill',
    delete_skill: 'Deleting a skill',
    list_mcp_servers: 'Checking MCP servers',
    create_mcp_server: 'Connecting an MCP server',
    update_mcp_server: 'Updating an MCP server',
    delete_mcp_server: 'Removing an MCP server',
    list_workflows: 'Checking playbooks and workflows',
    get_workflow: 'Reading a playbook',
    validate_playbook: 'Checking the playbook',
    save_playbook: 'Saving the playbook',
    validate_workflow_definition: 'Checking the workflow',
    save_workflow: 'Saving the workflow',
    delete_workflow: 'Deleting a workflow',
    list_codehooks: 'Checking functions',
    get_codehook: 'Reading a function',
    deploy_codehook: 'Deploying a function',
    create_api_tool: 'Creating an API tool',
    delete_codehook: 'Deleting a function',
    list_knowledge_sources: 'Checking knowledge',
    preview_knowledge_source: 'Reading knowledge',
    add_text_knowledge: 'Adding knowledge',
    add_web_knowledge: 'Adding web pages to knowledge',
    request_file_upload: 'Preparing a document upload',
    delete_knowledge_source: 'Deleting knowledge',
    write_todos: 'Planning',
    read_file: 'Reading notes',
    write_file: 'Writing notes',
    edit_file: 'Editing notes',
    ls: 'Looking through notes',
    list_applications: 'Finding your applications',
    create_application: 'Creating an application',
    emit_route: 'Opening the page',
};

const SUBAGENT_LABELS = {
    'workflow-builder': 'Playbook builder',
    'integration-builder': 'Integration builder',
    'knowledge-builder': 'Knowledge builder',
    'config-reviewer': 'Reviewer',
    'general-purpose': 'Assistant',
    workflow_builder: 'Playbook builder',
    integration_builder: 'Integration builder',
    knowledge_builder: 'Knowledge builder',
    config_reviewer: 'Reviewer',
};

export function toolLabel(name) {
    if (typeof name === 'string' && TOOL_LABELS[name]) return TOOL_LABELS[name];
    const words = String(name || 'Working').replace(/[_-]+/g, ' ').trim();
    return words.charAt(0).toUpperCase() + words.slice(1);
}

export function subagentLabel(name) {
    return SUBAGENT_LABELS[name] || toolLabel(name);
}

const newAssistant = runId => ({
    id: `assistant-${runId}`,
    role: 'assistant',
    runId,
    text: '',
    streaming: true,
    steps: [],
    todos: [],
    approvals: [],
    changes: [],
    navigations: [],
    uploads: [],
    suggestions: [],
    clarification: null,
    onboarding: null,
    setup: null,
    publishing: null,
    testing: null,
    notes: [],
    error: null,
    lastSeq: 0,
});

function withAssistant(state, runId, update) {
    const index = state.messages.findIndex(m => m.role === 'assistant' && m.runId === runId);
    const messages = [...state.messages];
    const current = index >= 0 ? messages[index] : newAssistant(runId);
    const next = update(current);
    if (index >= 0) messages[index] = next;
    else messages.push(next);
    return { ...state, messages };
}

const MAX_SUGGESTIONS = 3;

export function sanitizeSuggestions(items) {
    if (!Array.isArray(items)) return [];
    return items
        .filter(item => item && typeof item.label === 'string' && typeof item.message === 'string')
        .map(item => ({ label: item.label.trim().slice(0, 40), message: item.message.trim().slice(0, 200) }))
        .filter(item => item.label && item.message)
        .slice(0, MAX_SUGGESTIONS);
}

const SOURCE_ID = /^[a-z0-9][a-z0-9_-]{2,63}$/;
const DEFAULT_ACCEPT = ['pdf', 'doc', 'docx', 'csv', 'txt'];

/** An upload request from the network: only well-formed ids and plain file extensions. */
export function sanitizeUpload(data) {
    if (!data || typeof data.dataSourceId !== 'string' || !SOURCE_ID.test(data.dataSourceId)) return null;
    const accept = (Array.isArray(data.accept) ? data.accept : DEFAULT_ACCEPT)
        .filter(ext => typeof ext === 'string' && /^[a-z0-9]{1,8}$/.test(ext));
    return {
        dataSourceId: data.dataSourceId,
        description: typeof data.description === 'string' ? data.description.slice(0, 500) : '',
        ocr: Boolean(data.ocr),
        accept: accept.length ? accept : DEFAULT_ACCEPT,
    };
}

function updateStep(steps, id, patch) {
    return steps.map(step => (step.id === id ? { ...step, ...patch } : step));
}

export function applyEvent(state, event) {
    if (!event || typeof event !== 'object' || !event.type || !event.runId) return state;
    const { type, runId } = event;
    const seq = Number.isFinite(event.seq) ? event.seq : 0;
    const data = event.data && typeof event.data === 'object' ? event.data : {};
    const existing = state.messages.find(m => m.role === 'assistant' && m.runId === runId);
    // Events are delivered in order; drop redeliveries.
    if (existing && seq && seq <= existing.lastSeq) return state;

    const next = withAssistant(state, runId, message => {
        const m = { ...message, lastSeq: seq || message.lastSeq };
        switch (type) {
            case 'run.started':
                return m;
            case 'message.delta':
                return { ...m, text: m.text + (typeof data.text === 'string' ? data.text : '') };
            case 'tool.started':
                if (data.name === 'write_todos') return m; // shown as the plan instead
                return { ...m, steps: [...m.steps, { id: data.id, kind: 'tool', label: toolLabel(data.name), status: 'running' }] };
            case 'tool.finished':
                return { ...m, steps: updateStep(m.steps, data.id, { status: data.ok ? 'done' : 'failed', error: data.error || null }) };
            case 'subagent.started':
                return { ...m, steps: [...m.steps, { id: data.id, kind: 'subagent', label: subagentLabel(data.subagent), detail: data.task, status: 'running' }] };
            case 'subagent.finished':
                return { ...m, steps: updateStep(m.steps, data.id, { status: data.ok ? 'done' : 'failed' }) };
            case 'todos.updated':
                return { ...m, todos: Array.isArray(data.todos) ? data.todos : m.todos };
            case 'resource.changed':
                return { ...m, changes: [...m.changes, { kind: data.kind, action: data.action, name: data.name || data.id }] };
            case 'testing.plan':
            case 'testing.run':
                return { ...m, testing: sanitizeTesting(data) || m.testing };
            case 'clarification.requested':
                return { ...m, clarification: sanitizeClarification(data) || m.clarification };
            case 'onboarding.card':
                return { ...m, onboarding: sanitizeOnboardingCard(data) || m.onboarding };
            case 'publish.card':
                return { ...m, publishing: sanitizePublishCard(data) || m.publishing };
            case 'onboarding.setup':
                return { ...m, setup: sanitizeSetup(data) || m.setup };
            case 'upload.requested': {
                const upload = sanitizeUpload(data);
                if (!upload || (m.uploads || []).some(u => u.dataSourceId === upload.dataSourceId)) return m;
                return { ...m, uploads: [...(m.uploads || []), upload] };
            }
            case 'action':
                if (data.type === 'ROUTE' && typeof data.path === 'string') {
                    const label = typeof data.label === 'string' && data.label ? data.label : data.path;
                    return { ...m, navigations: [...(m.navigations || []), { label, path: data.path }] };
                }
                return m;
            case 'suggestions':
                return { ...m, suggestions: sanitizeSuggestions(data.items) };
            case 'approval.required':
                return { ...m, approvals: Array.isArray(data.requests) ? data.requests.filter(r => r && typeof r === 'object') : [] };
            case 'run.completed':
                return {
                    ...m,
                    text: typeof data.text === 'string' && data.text ? data.text : m.text,
                    streaming: false,
                    steps: m.steps.map(s => (s.status === 'running' ? { ...s, status: 'done' } : s)),
                    approvals: data.status === 'awaiting_approval' ? m.approvals : [],
                };
            case 'run.failed':
                return {
                    ...m,
                    streaming: false,
                    error: typeof data.error === 'string' && data.error ? data.error : 'Something went wrong.',
                    steps: m.steps.map(s => (s.status === 'running' ? { ...s, status: 'failed' } : s)),
                };
            default:
                return m;
        }
    });

    if (type === 'run.completed') {
        return { ...next, running: false, awaitingApproval: data.status === 'awaiting_approval', error: null };
    }
    if (type === 'run.failed') return { ...next, running: false, awaitingApproval: false };
    if (type === 'approval.required') return { ...next, awaitingApproval: true };
    // Late replies and progress on a completed turn must not lock input again.
    if (type === 'suggestions' || (type !== 'run.started' && existing && !existing.streaming)) return next;
    return { ...next, running: true };
}

let localCounter = 0;

export function reducer(state, action) {
    switch (action.type) {
        case 'reset':
            return initialState;
        case 'hydrate': {
            // Transcript rows from getCopilotSession: {role, content, messageId, approvals?}
            // Never trust stored history's shape: a bad transcript must not break the page.
            const rows = (Array.isArray(action.messages) ? action.messages : [])
                .filter(row => row && typeof row === 'object' && (row.role === 'user' || row.role === 'assistant'));
            const messages = rows.map((row, index) => (row.role === 'user'
                ? { id: `user-h${index}`, role: 'user', text: String(row.content ?? '') }
                : { ...newAssistant(`history-${index}`), text: String(row.content ?? ''), streaming: false, clarification: sanitizeClarification(row.clarification), onboarding: sanitizeOnboardingCard(row.onboarding), publishing: sanitizePublishCard(row.publishing), setup: row.setup ? sanitizeSetup(row.setup) : null, testing: sanitizeTesting(row.testing) }));
            const last = rows[rows.length - 1];
            const awaitingApproval = action.status === 'awaiting_approval' && last?.role === 'assistant';
            if (awaitingApproval && messages.length) {
                messages[messages.length - 1].approvals = Array.isArray(last.approvals) ? last.approvals : [];
            }
            return { ...initialState, messages, awaitingApproval, running: !messages[messages.length - 1]?.clarification && (action.status === 'running' || action.status === 'queued') };
        }
        case 'user_sent':
            localCounter += 1;
            return {
                ...state,
                running: true,
                awaitingApproval: false,
                error: null,
                messages: [
                    // Sending anything answers the last reply: its approvals and suggestions are done.
                    ...state.messages.map(m => (m.approvals?.length || m.suggestions?.length
                        ? { ...m, approvals: [], suggestions: [] } : m)),
                    ...(action.text ? [{ id: `user-${Date.now()}-${localCounter}`, role: 'user', text: action.text }] : []),
                ],
            };
        case 'send_failed': {
            // Restore the question after an optimistic answer could not be sent.
            const last = state.messages[state.messages.length - 1];
            const previous = state.messages[state.messages.length - 2];
            const messages = last?.role === 'user' && previous?.clarification
                ? state.messages.slice(0, -1) : state.messages;
            return { ...state, messages, running: false, error: action.error };
        }
        case 'event':
            return applyEvent(state, action.event);
        default:
            return state;
    }
}

/**
 * The reducer React uses. Any exception (a malformed event, unexpected history shape)
 * becomes an error shown in the chat instead of an exception thrown during render.
 */
export function safeReducer(state, action) {
    try {
        return reducer(state, action);
    } catch (error) {
        console.error('[copilot] could not apply', action?.type, error);
        return { ...(state || initialState), running: false, error: 'Something went wrong showing the copilot\'s reply. Start a new chat if this continues.' };
    }
}
